(() => {
  const API_NAME = 'NordwegOwnerReviewsLoader';
  const CONFIG_SELECTOR = 'script[data-owner-reviews-loader]';
  // A loading section keeps its final height, invisibly, until it resolves (see its stylesheet).
  // Once this loader runs it owns that reservation: a failed runtime releases it at once, and none
  // outlives 20 s after loading starts for it (runtime download plus the runtime's own 10 s feed
  // abort). A release is final; the runtime never renders a released section.
  const RESERVED_SECTION_SELECTOR = '.nw-owner-reviews[data-owner-reviews-source-status="loading"]';
  const RESERVATION_LIMIT_MS = 20000;

  if (window[API_NAME]?.refresh) {
    window[API_NAME].refresh();
    return;
  }

  const observedSentinels = new WeakSet();
  const nearSentinels = new Set();
  let observer = null;
  let runtimeUrl = '';
  let runtimePromise = null;
  let loaded = false;
  const sectionsWithDeadline = new WeakSet();

  function configurations() {
    return [...document.querySelectorAll(CONFIG_SELECTOR)];
  }

  function currentConfig() {
    const configs = configurations();
    return configs[configs.length - 1] || null;
  }

  function isLoaded() {
    return loaded;
  }

  function reservedSections() {
    return [...document.querySelectorAll(RESERVED_SECTION_SELECTOR)];
  }

  function releaseReservations(sections = reservedSections()) {
    sections.forEach((section) => {
      if (section.dataset.ownerReviewsSourceStatus === 'loading') {
        section.dataset.ownerReviewsSourceStatus = 'unavailable';
      }
    });
  }

  // Takes over the stylesheet's 20 s fallback. If that fallback already gave the space back (this
  // loader ran very late), keep it given back rather than restoring the reservation.
  function guardReservations() {
    reservedSections().forEach((section) => {
      if (window.getComputedStyle(section).maxHeight === '0px') {
        releaseReservations([section]);
        return;
      }
      section.dataset.ownerReviewsGuarded = 'true';
    });
  }

  // Each section gets its own deadline once loading has started for it, so a section the Theme
  // Editor inserts later is never released early by an older section's deadline.
  function armDeadlines() {
    reservedSections().forEach((section) => {
      if (sectionsWithDeadline.has(section)) return;
      sectionsWithDeadline.add(section);
      window.setTimeout(() => releaseReservations([section]), RESERVATION_LIMIT_MS);
    });
  }

  function load() {
    if (loaded) return Promise.resolve();
    if (runtimePromise) return runtimePromise;

    const config = currentConfig();
    runtimeUrl = config?.dataset.ownerReviewsScript || runtimeUrl;
    if (!runtimeUrl) {
      releaseReservations();
      return Promise.reject(new Error('Owner reviews runtime URL is unavailable.'));
    }

    runtimePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = runtimeUrl;
      script.async = true;
      script.dataset.ownerReviewsRuntime = '';

      const settle = (event) => {
        script.removeEventListener('load', settle);
        script.removeEventListener('error', settle);
        if (event.type === 'error') {
          script.remove();
          reject(new Error(`Failed to load owner reviews runtime: ${runtimeUrl}`));
          return;
        }
        loaded = true;
        resolve();
      };

      script.addEventListener('load', settle);
      script.addEventListener('error', settle);
      (document.head || document.documentElement).appendChild(script);
    }).then(() => {
      observer?.disconnect();
      nearSentinels.clear();
    }).catch((error) => {
      runtimePromise = null;
      releaseReservations();
      throw error;
    });
    armDeadlines();

    return runtimePromise;
  }

  function decodedHash() {
    const hash = window.location?.hash?.slice(1) || '';
    try {
      return decodeURIComponent(hash);
    } catch (_error) {
      return hash;
    }
  }

  function targetsCurrentHash(configs = configurations()) {
    const hash = decodedHash();
    return Boolean(hash && configs.some((config) => config.dataset.ownerReviewsSectionId === hash));
  }

  function observeSentinel(sentinel) {
    if (!sentinel || observedSentinels.has(sentinel) || loaded) return;
    observedSentinels.add(sentinel);
    observer.observe(sentinel);
  }

  function refresh() {
    const configs = configurations();
    const latestConfig = configs[configs.length - 1];
    runtimeUrl = latestConfig?.dataset.ownerReviewsScript || runtimeUrl;
    guardReservations();
    if (loaded || runtimePromise) armDeadlines();
    if (loaded || configs.length === 0) return;

    if (configs.some((config) => config.dataset.designMode === 'true') || targetsCurrentHash(configs)) {
      load().catch(() => false);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      load().catch(() => false);
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) nearSentinels.add(entry.target);
          else nearSentinels.delete(entry.target);
        });
        if (nearSentinels.size > 0) load().catch(() => false);
      }, { rootMargin: '1800px 0px', threshold: 0 });
    }

    configs.forEach((config) => {
      observeSentinel(document.getElementById(config.dataset.ownerReviewsSentinelId));
    });
  }

  function handleHashChange() {
    if (targetsCurrentHash()) load().catch(() => false);
  }

  const api = { isLoaded, load, refresh };
  window[API_NAME] = api;

  window.addEventListener('hashchange', handleHashChange);
  document.addEventListener('shopify:section:load', refresh);
  refresh();
})();
